import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { login } from '../../config/previewAccess';
import {
  LandingShell, Narrow, Panel, Eyebrow, H2, Sub, Field, ErrorText,
  PrimaryButton, PrimaryLink, T,
} from './ui';

const FiArrowRight = FiIcons.FiArrowRight as React.ComponentType<any>;
const FiClock = FiIcons.FiClock as React.ComponentType<any>;

/**
 * General sign-in for prospects. Authenticates against the backend
 * (/api/preview-auth/login); only an APPROVED request yields a token → /my-apps.
 * A pending/rejected request gets an informative message.
 */
const SignIn: React.FC = () => {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null); setPending(false); setBusy(true);
    try {
      const res = await login(email, password);
      if (res.ok) { nav('/my-apps'); return; }
      if (res.code === 'PENDING') { setPending(true); return; }
      setError(res.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <LandingShell>
      <Narrow>
        <Panel style={{ marginTop: 24 }}>
          <Head>
            <Eyebrow>Welcome back</Eyebrow>
            <H2>Sign in to ServiceBuds</H2>
            <Sub>Use the email and password you signed up with.</Sub>
          </Head>
          <form onSubmit={onSubmit} noValidate>
            <Field><span className="lbl">Work email</span>
              <input type="email" value={email} placeholder="you@company.com"
                onChange={e => { setEmail(e.target.value); setPending(false); }} /></Field>
            <Spacer><Field><span className="lbl">Password</span>
              <input type="password" value={password} placeholder="Your password"
                onChange={e => setPassword(e.target.value)} /></Field></Spacer>
            {error && <Spacer><ErrorText role="alert">{error}</ErrorText></Spacer>}
            {pending && (
              <Spacer><PendingBox role="status">
                <FiClock /> Your request is still <b>pending team review</b>. You’ll be able to sign in once it’s approved.
              </PendingBox></Spacer>
            )}
            <Spacer><PrimaryButton type="submit" disabled={busy} style={{ width: '100%' }}>
              {busy ? 'Signing in…' : <>Sign in <FiArrowRight /></>}
            </PrimaryButton></Spacer>
          </form>
          <Divider><span>New to ServiceBuds?</span></Divider>
          <PrimaryLink to="/signup" style={{ width: '100%', background: '#fff', color: T.accentDark, border: `1px solid ${T.border}`, boxShadow: 'none' }}>
            Request preview access
          </PrimaryLink>
        </Panel>
      </Narrow>
    </LandingShell>
  );
};

export default SignIn;

const Head = styled.div`margin-bottom: 18px;`;
const Spacer = styled.div`margin-top: 14px;`;
const PendingBox = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 600; color: #9A6510;
  background: ${T.warnBg}; border-radius: 10px; padding: 10px 12px;
`;
const Divider = styled.div`
  display: flex; align-items: center; text-align: center; gap: 12px;
  margin: 20px 0 14px; color: ${T.muted}; font-size: 12.5px;
  &::before, &::after { content: ''; flex: 1; height: 1px; background: #E1E6E6; }
`;
